// layouts/AdminLayout.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, X, LayoutDashboard, Bus, Calendar, 
  Users, CreditCard, LogOut, Ticket,
  User, ChevronDown, Settings, DollarSign, Save, Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDivisas } from '../contexts/DivisasContext';
import { showSuccess, showError } from '../utils/toast';

const API_URL = process.env.REACT_APP_API_URL;

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [divisasOpen, setDivisasOpen] = useState(false);
  
  // ✅ Usar el contexto global (que ya carga del backend)
  const { divisas: divisasGlobal, cargarDivisas } = useDivisas();
  
  // ✅ Estado local para editar (temporal hasta guardar)
  const [divisas, setDivisas] = useState({
    bolivares: 0,
    reales: 0
  });
  const [divisasGuardadas, setDivisasGuardadas] = useState({
    bolivares: 0,
    reales: 0
  });
  const [guardandoDivisas, setGuardandoDivisas] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();

  // ✅ Sincronizar el estado local cuando el contexto se carga/actualiza
  useEffect(() => {
    setDivisas(divisasGlobal);
    setDivisasGuardadas(divisasGlobal);
  }, [divisasGlobal]);

  // ✅ Detectar cambios sin guardar
  const hayCambiosSinGuardar = 
    divisas.bolivares !== divisasGuardadas.bolivares ||
    divisas.reales !== divisasGuardadas.reales;

  // ✅ Guardar divisas en el backend
  const handleGuardarDivisas = async () => {
    try {
      setGuardandoDivisas(true);
      
      const bolivares = parseFloat(divisas.bolivares) || 0;
      const reales = parseFloat(divisas.reales) || 0;
      
      if (bolivares < 0 || reales < 0) {
        showError('Los valores no pueden ser negativos');
        setGuardandoDivisas(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/admin/divisas`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bolivares, reales })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error del backend:', errorText);
        throw new Error('Error al guardar las divisas');
      }

      const result = await response.json();
      
      const nuevasDivisas = {
        bolivares: result.bolivares,
        reales: result.reales
      };
      
      setDivisas(nuevasDivisas);
      setDivisasGuardadas(nuevasDivisas);
      
      // ✅ Recargar el contexto global para que todas las vistas se actualicen
      await cargarDivisas();
      
      showSuccess('Valores de divisas actualizados');
      
    } catch (error) {
      console.error('Error al guardar divisas:', error);
      showError('No se pudieron guardar las divisas');
    } finally {
      setGuardandoDivisas(false);
    }
  };

  const handleDivisaChange = (campo, valor) => {
    setDivisas(prev => ({
      ...prev,
      [campo]: valor === '' ? '' : valor
    }));
  };

  const menuItems = [
    { path: '/admin/servicios', icon: Bus, label: 'Servicios' },
    { path: '/admin/itinerario', icon: Calendar, label: 'Itinerario' },
    { path: '/admin/reservas', icon: Ticket, label: 'Reservas' },
    { path: '/admin/configuracion', icon: Settings, label: 'Configuración' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getInitials = () => {
    if (!admin?.nombre) return 'A';
    if (admin.nombre === 'Administrador') return 'AD';
    const nombres = admin.nombre.split(' ');
    if (nombres.length >= 2) {
      return `${nombres[0][0]}${nombres[1][0]}`.toUpperCase();
    }
    return nombres[0][0].toUpperCase();
  };

  const formatDivisaHeader = (valor) => {
    if (valor === '' || valor === null || valor === undefined) return '—';
    const num = parseFloat(valor);
    if (isNaN(num)) return '—';
    return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(num);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#1a1a2e] text-white"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 w-64 h-screen bg-[#1a1a2e] text-white transition-transform
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-5 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Bus className="w-6 h-6 text-[#e83e8c]" />
            <div>
              <h1 className="text-lg font-bold">Transporte</h1>
              <p className="text-xs text-[#e83e8c] font-semibold">Tren al Sur</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Panel de Administración</p>
        </div>
        
        <nav className="p-4">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors
                ${isActive(item.path) 
                  ? 'bg-[#e83e8c] text-white' 
                  : 'text-gray-300 hover:bg-[#2d2d44] hover:text-white'}
              `}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg mt-4 text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar sesión</span>
          </button>
        </nav>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Top bar con divisas + usuario */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-6 py-3 flex items-center justify-between gap-4">
            
            {/* ✅ Widget de Divisas */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDivisasOpen(!divisasOpen)}
                className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm
                  ${hayCambiosSinGuardar 
                    ? 'bg-yellow-50 border border-yellow-300 text-yellow-800 hover:bg-yellow-100' 
                    : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100'}
                `}
                title="Configurar valores de divisas"
              >
                <DollarSign className="w-4 h-4 text-[#e83e8c]" />
                <span className="font-medium">Divisas:</span>
                <span className="text-gray-500">$1 USD =</span>
                <span className="font-semibold text-[#e83e8c]">
                  {formatDivisaHeader(divisasGuardadas.bolivares)} Bs
                </span>
                <span className="text-gray-400">·</span>
                <span className="font-semibold text-[#e83e8c]">
                  {formatDivisaHeader(divisasGuardadas.reales)} R$
                </span>
                {hayCambiosSinGuardar && (
                  <span className="ml-1 w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                )}
              </button>
            </div>

            {/* ✅ Panel desplegable de divisas */}
            {divisasOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setDivisasOpen(false)}
                />
                <div className="absolute top-16 left-6 w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-pink-50 rounded-lg">
                        <DollarSign className="w-4 h-4 text-[#e83e8c]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">Configuración de Divisas</h3>
                        <p className="text-xs text-gray-500">Tasa de cambio base: 1 USD</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {/* USD (fijo) */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Dólar estadounidense (base)
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 flex items-center gap-2">
                          <span className="font-semibold">$</span>
                          <span>1.00</span>
                          <span className="text-xs text-gray-400 ml-auto">USD (fijo)</span>
                        </div>
                      </div>
                    </div>

                    {/* Bolívares */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Bolívares (VES)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">
                          Bs
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={divisas.bolivares}
                          onChange={(e) => handleDivisaChange('bolivares', e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-12 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Reales */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Reales brasileños (BRL)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">
                          R$
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={divisas.reales}
                          onChange={(e) => handleDivisaChange('reales', e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-12 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                      <p className="text-xs text-blue-700">
                        💡 Estos valores se guardan en el servidor y se aplican a todas las vistas del sistema.
                      </p>
                    </div>
                  </div>

                  {/* Botones */}
                  <div className="p-4 border-t border-gray-100 flex gap-2">
                    <button
                      onClick={() => {
                        setDivisas(divisasGuardadas);
                        setDivisasOpen(false);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleGuardarDivisas}
                      disabled={!hayCambiosSinGuardar || guardandoDivisas}
                      className={`
                        flex-1 px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors
                        ${hayCambiosSinGuardar && !guardandoDivisas
                          ? 'bg-[#e83e8c] hover:bg-[#c2185b] text-white'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
                      `}
                    >
                      {guardandoDivisas ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Guardar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Menú de usuario */}
            <div className="relative ml-auto">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#e83e8c] text-white flex items-center justify-center text-sm font-medium">
                  {getInitials()}
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-gray-800">
                    {admin?.nombre || 'Administrador'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {admin?.role || 'Admin'}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
              </button>
              
              {userMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-800">
                        {admin?.nombre || 'Administrador'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {admin?.email || 'admin@centralpasajes.com'}
                      </p>
                    </div>
                    <div className="p-2">
                      <hr className="my-1" />
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <LogOut className="w-4 h-4" />
                        Cerrar sesión
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-4">
          {children}
        </div>
      </main>

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;